// src/components/login/LoginForm.tsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Location } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { EmailInput } from './EmailInput';
import { PasswordInput } from './PasswordInput';
import { SocialLogin } from './SocialLogin';
import { SANDBOX_AUTOLOGIN, SANDBOX_EMAIL, SANDBOX_PASSWORD } from '../../config/env';
import styles from './css/LoginForm.module.css';

type LocationState = { from?: Location; oauthError?: string };

// Wrapper: choose sandbox autologin or normal form. No hooks here.
export const LoginForm: React.FC = () => {
    return SANDBOX_AUTOLOGIN ? <SandboxAutoLogin /> : <LoginFormInner />;
};

// --- SANDBOX: do the token call first, then navigate ---
const SandboxAutoLogin: React.FC = () => {
    const { isAuthenticated, signInLocal } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const fromPath = (location.state as LocationState | undefined)?.from?.pathname || '/';

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const started = useRef(false);

    useEffect(() => {
        if (isAuthenticated) {
            navigate(fromPath, { replace: true });
            return;
        }
        if (started.current) return;
        started.current = true;

        (async () => {
            if (!SANDBOX_EMAIL || !SANDBOX_PASSWORD) {
                setError('Sandbox credentials are missing.');
                return;
            }
            setLoading(true);
            const ok = await signInLocal(SANDBOX_EMAIL, SANDBOX_PASSWORD);
            setLoading(false);
            if (ok) {
                navigate(fromPath, { replace: true });
            } else {
                setError('Failed to start sandbox session. Please try again.');
            }
        })();
    }, [isAuthenticated, navigate, fromPath, signInLocal]);

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.card}>
                <h1 className={styles.title}>
                    <span className={styles.main}>REACTIVE</span>
                    <span className={styles.highlight}>DESK</span>
                </h1>
                {error ? (
                    <>
                        <p className={styles.error}>{error}</p>
                        <button
                            className={styles.signIn}
                            onClick={() => { started.current = false; setError(null); }}
                        >
                            Retry
                        </button>
                    </>
                ) : (
                    <p style={{ textAlign: 'center', opacity: 0.8 }}>
                        {loading ? 'Starting sandbox session…' : 'Preparing sandbox…'}
                    </p>
                )}
            </div>
        </div>
    );
};

// --- NORMAL login form (non-sandbox) ---
const LoginFormInner: React.FC = () => {
    const { signInLocal } = useContext(AuthContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const fromPath = (location.state as LocationState | undefined)?.from?.pathname || '/';

    useEffect(() => {
        const oauthError = (location.state as LocationState | undefined)?.oauthError;
        if (oauthError) {
            setError(oauthError);
            navigate(location.pathname, { replace: true });
        }
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        if (error && cardRef.current) {
            cardRef.current.classList.add(styles.errorShake);
            const t = setTimeout(() => cardRef.current?.classList.remove(styles.errorShake), 600);
            return () => clearTimeout(t);
        }
    }, [error]);

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setError(null);
        setLoading(true);
        const ok = await signInLocal(email, password);
        setLoading(false);
        if (!ok) { setError('Invalid credentials'); return; }
        navigate(fromPath, { replace: true });
    };

    return (
        <div className={styles.pageWrapper}>
            <div ref={cardRef} className={styles.card}>
                <h1 className={styles.title}>
                    <span className={styles.main}>REACTIVE</span>
                    <span className={styles.highlight}>DESK</span>
                </h1>

                <form onSubmit={handleSubmit}>
                    <EmailInput value={email} onChange={(v) => { setEmail(v); if (error) setError(null); }} />
                    <PasswordInput value={password} onChange={(v) => { setPassword(v); if (error) setError(null); }} />
                    {error && <p className={styles.error}>{error}</p>}
                    <button className={styles.signIn} type="submit" disabled={loading || !email || !password}>
                        {loading ? 'Signing In…' : 'Sign In'}
                    </button>
                </form>

                <div className={styles.orContainer}>
                    <span className={styles.orText}>Or sign in with</span>
                </div>
                <SocialLogin />
            </div>
        </div>
    );
};
